import {useAgencySubbrandsQuery} from "@/api/agencyApi";
import {useCompanyCampaignsQuery} from "@/api/companyApi";
import CampaignList from "@/components/Campaign/CampaignList";
import Button from "@/components/Common/Button";
import FullPageLoader from "@/components/Common/FullPageLoader";
import CompanyGroups from "@/components/Company/CompanyGroups";
import AgencyInvite from "@/components/MultiAccount/AgencyInvite";
import MembersList from "@/components/MultiAccount/MembersList";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import {createFileRoute} from "@tanstack/react-router";
import {getDefaultStore} from "jotai";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/_agency/agency/$agencyId/dashboard")({
  component: AgencyDashboard,
  params: {
    parse: raw => ({agencyId: +raw.agencyId}),
    stringify: params => ({agencyId: params.agencyId?.toString()}),
  },
  loader: () => {
    getDefaultStore().set(selectedCompanyIdAtom, null);
  },
});

function AgencyDashboard() {
  const {t} = useTranslation(["multi_account", "navigation"]);
  const navigate = Route.useNavigate();
  const {agencyId} = Route.useParams();
  const isAdmin = useIsAdmin();

  useTitle(t("navigation:campaigns"));

  const {data: subBrands, isPending: isFetchingSubBrands} = useAgencySubbrandsQuery({
    agencyId: Number(agencyId),
  });
  const {data: activeCampaignsData, isPending: isFetchingActiveCampaigns} = useCompanyCampaignsQuery({
    agencyId: Number(agencyId),
    status: "active",
    queryOptions: {
      enabled: !!agencyId,
      refetchOnMount: true,
    },
  });
  const {data: openCampaignsData, isPending: isFetchingOpenCampaigns} = useCompanyCampaignsQuery({
    agencyId: Number(agencyId),
    status: "open",
    queryOptions: {
      enabled: !!agencyId,
      refetchOnMount: true,
    },
  });

  if (isFetchingSubBrands || isFetchingActiveCampaigns || isFetchingOpenCampaigns) {
    return <FullPageLoader />;
  }

  return (
    <div className="max-w-wide container mx-auto mt-8 mb-20 flex flex-col justify-center gap-7 px-8 pt-10 pb-20">
      {isAdmin && (
        <Button
          onClick={() => navigate({to: "/agency/$agencyId/customers", params: {agencyId}})}
          buttonStyle="Blue"
          className="h-auto w-fit px-8">
          {t("agency_subbrands")}
        </Button>
      )}
      {subBrands?.length === 0 || (!activeCampaignsData?.count && !openCampaignsData?.count) ? (
        <div className="flex flex-col items-center justify-center gap-5">
          <AgencyInvite />
          <MembersList />
        </div>
      ) : (
        <>
          <CampaignList type="active" />
          <CampaignList type="open" />
        </>
      )}
      <CompanyGroups agencyId={Number(agencyId)} compactFirstPage />
    </div>
  );
}
